import { RESIDENCY_OPTIONS } from './residency';
import { AsYouType, parsePhoneNumberFromString, validatePhoneNumberLength, type CountryCode } from 'libphonenumber-js';

// ITU-T E.164 international calling codes, keyed by the same lowercase ISO
// 3166-1 alpha-2 codes as residency.ts's COUNTRY_CODES, so the phone
// country-selector and the residency picker draw on one consistent code list
// rather than two that can drift apart.
const DIAL_CODES: Record<string, string> = {
    ad: '376', ae: '971', af: '93', ag: '1', al: '355', am: '374', ao: '244', ar: '54', at: '43', au: '61', az: '994',
    ba: '387', bb: '1', bd: '880', be: '32', bf: '226', bg: '359', bh: '973', bi: '257', bj: '229', bn: '673', bo: '591', br: '55', bs: '1', bt: '975', bw: '267', by: '375', bz: '501',
    ca: '1', cd: '243', cf: '236', cg: '242', ch: '41', ci: '225', cl: '56', cm: '237', cn: '86', co: '57', cr: '506', cu: '53', cv: '238', cy: '357', cz: '420',
    de: '49', dj: '253', dk: '45', dm: '1', do: '1', dz: '213',
    ec: '593', ee: '372', eg: '20', er: '291', es: '34', et: '251',
    fi: '358', fj: '679', fr: '33',
    ga: '241', gb: '44', gd: '1', ge: '995', gh: '233', gm: '220', gn: '224', gq: '240', gr: '30', gt: '502', gw: '245', gy: '592',
    hn: '504', hr: '385', ht: '509', hu: '36',
    id: '62', ie: '353', il: '972', in: '91', iq: '964', ir: '98', is: '354', it: '39',
    jm: '1', jo: '962', jp: '81',
    ke: '254', kg: '996', kh: '855', ki: '686', km: '269', kn: '1', kp: '850', kr: '82', kw: '965', kz: '7',
    la: '856', lb: '961', lc: '1', li: '423', lk: '94', lr: '231', ls: '266', lt: '370', lu: '352', lv: '371', ly: '218',
    ma: '212', mc: '377', md: '373', me: '382', mg: '261', mh: '692', mk: '389', ml: '223', mm: '95', mn: '976', mr: '222', mt: '356', mu: '230', mv: '960', mw: '265', mx: '52', my: '60', mz: '258',
    na: '264', ne: '227', ng: '234', ni: '505', nl: '31', no: '47', np: '977', nz: '64',
    om: '968',
    pa: '507', pe: '51', pg: '675', ph: '63', pk: '92', pl: '48', pt: '351', pw: '680', py: '595',
    qa: '974',
    ro: '40', rs: '381', ru: '7', rw: '250',
    sa: '966', sb: '677', sc: '248', sd: '249', se: '46', sg: '65', si: '386', sk: '421', sl: '232', sm: '378', sn: '221', so: '252', sr: '597', ss: '211', sv: '503', sy: '963', sz: '268',
    td: '235', tg: '228', th: '66', tj: '992', tl: '670', tm: '993', tn: '216', to: '676', tr: '90', tt: '1', tv: '688', tw: '886', tz: '255',
    ua: '380', ug: '256', us: '1', uy: '598', uz: '998',
    va: '39', vc: '1', ve: '58', vn: '84', vu: '678',
    ws: '685',
    ye: '967',
    za: '27', zm: '260', zw: '263'
};

function dialCodeFor(residencyCode: string): string {
    return DIAL_CODES[residencyCode.toLowerCase()] ?? '1';
}

// One shared list for every phone country-code Select in the app, built once
// from the same residency country list rather than a second hand-maintained
// one, so the two never drift apart.
const PHONE_COUNTRY_OPTIONS = RESIDENCY_OPTIONS.map((option) => ({
    value: option.code,
    label: `${option.name} (+${dialCodeFor(option.code)})`,
    hint: `+${dialCodeFor(option.code)}`
}));

// Keep the picker used by the app, but use maintained numbering metadata for
// national formatting and possible-length checks. The parsed E.164 value is
// what the booking and payment APIs receive.
function formatPhoneDigits(input: string, country: string): string {
    const code = country.toUpperCase() as CountryCode;
    // India's metadata includes longer service numbers; hotel contact numbers
    // use the 10-digit national mobile format.
    let digits = input.replace(/\D/g, '').slice(0, country.toLowerCase() === 'in' ? 10 : 15 - dialCodeFor(country).length);
    // E.164's 15 digit ceiling is too generous for many national numbers.
    // Trim to the selected country's *possible* length before formatting.
    while (digits && validatePhoneNumberLength(digits, code) === 'TOO_LONG') digits = digits.slice(0, -1);
    return new AsYouType(country.toUpperCase() as CountryCode).input(digits);
}

function parsePhoneDigits(input: string, country: string): { possible: boolean; e164: string } {
    const number = parsePhoneNumberFromString(input, country.toUpperCase() as CountryCode);
    const nationalDigits = input.replace(/\D/g, '');
    return { possible: (number?.isPossible() ?? false) && (country.toLowerCase() !== 'in' || nationalDigits.length === 10), e164: number?.number ?? '' };
}

export { DIAL_CODES, PHONE_COUNTRY_OPTIONS, dialCodeFor, formatPhoneDigits, parsePhoneDigits };
